import React from 'react';
import { ArrowRight, Users, Target, Lightbulb, Award, CheckCircle, Star } from 'lucide-react';
import WhatmakesUsUnique from './WhatmakesUsUnique';

export default function AboutUsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Compelling Headline */}
      <section className="relative overflow-hidden px-6 py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full -translate-y-48 translate-x-48 opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-purple-100 to-pink-100 rounded-full translate-y-32 -translate-x-32 opacity-60"></div>
        
        <div className="relative max-w-6xl mx-auto text-center mt-5">
          <div className="mb-5">
            <span className="inline-block px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-full shadow-lg">
              Crafting Excellence Since 2018
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            Building Tomorrow's
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {" "}Success{" "}
            </span>
            Today
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-12 leading-relaxed font-poppins" >
            We partner with visionary companies to create innovative solutions that drive growth, transform industries, and deliver extraordinary results through cutting-edge technology and strategic expertise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 flex items-center gap-2">
              Explore Our Story
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 hover:border-gray-400 transition-all duration-300">
              Watch Our Journey
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: "500+", label: "Projects Delivered" },
              { number: "150+", label: "Happy Clients" },
              { number: "25+", label: "Countries Reached" },
              { number: "99%", label: "Success Rate" }
            ].map((stat, index) => (
              <div key={index} className="group">
                <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Story Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="space-y-8">
              <div>
                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
                  Our Journey
                </span>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  From Vision to
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {" "}Global Impact
                  </span>
                </h2>
              </div>
              
              <p className="text-lg text-gray-600 leading-relaxed font-poppins">
                What started as a bold vision in 2018 has evolved into a transformative force in the digital landscape. Founded by a team of passionate innovators, we began with a simple belief: that technology should empower businesses to achieve the extraordinary.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed font-poppins">
                Today, we're proud to be trusted partners to Fortune 500 companies and ambitious startups alike, delivering solutions that don't just meet expectations—they redefine what's possible.
              </p>

              <div className="space-y-4 font-poppins">
                {[
                  "Award-winning design and development",
                  "Cutting-edge technology solutions",
                  "Dedicated customer success focus",
                  "Global team of expert professionals"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl">
                <div className="text-center mb-6">
                  <Star className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Industry Recognition</h3>
                  <p className="text-gray-600">Trusted by leading organizations worldwide</p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900">Best Innovation Award</span>
                    <span className="text-blue-600 font-semibold">2023</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900">Top Employer Certification</span>
                    <span className="text-blue-600 font-semibold">2024</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900">Client Choice Award</span>
                    <span className="text-blue-600 font-semibold">2024</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission & Vision Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-600 leading-relaxed font-poppins">
                To empower businesses with innovative technology solutions that drive meaningful growth and create lasting impact. We believe in building partnerships that transform challenges into opportunities and visions into reality.
              </p>
            </div>
            
            <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-600 leading-relaxed font-poppins">
                To be the world's most trusted technology partner, pioneering solutions that shape the future of business and create a more connected, efficient, and innovative world for everyone.
              </p>
            </div>
          </div>

        <section>
            <WhatmakesUsUnique/>
        </section>

          {/* Leadership Team */}
          <div className="text-center mb-12 mt-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-4">
              Meet Our Team
            </span>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Visionary
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {" "}Leadership
              </span>
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-poppins">
              Our diverse leadership team brings together decades of experience, innovative thinking, and a shared commitment to excellence that drives everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                name: "Sarah Chen", 
                role: "CEO & Founder", 
                image: "SC",
                description: "Visionary leader with 15+ years in tech innovation"
              },
              { 
                name: "Marcus Johnson", 
                role: "Chief Technology Officer", 
                image: "MJ",
                description: "Tech pioneer specializing in scalable solutions"
              },
              { 
                name: "Elena Rodriguez", 
                role: "Head of Design", 
                image: "ER",
                description: "Award-winning designer focused on user experience"
              }
            ].map((member, index) => (
              <div key={index} className="group text-center bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="relative mb-6">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {member.image}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-3 border-white"></div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h4>
                <p className="text-blue-600 font-semibold mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-200 to-pink-200 rounded-full translate-y-12 -translate-x-12 opacity-50"></div>
            
            <div className="relative">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Ready to Transform Your Business?
              </h3>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-poppins">
                Join hundreds of successful companies who have accelerated their growth with our innovative solutions. Let's build something amazing together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center gap-2">
                  Start Your Project
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 hover:border-gray-400 transition-all duration-300">
                  Schedule Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
    </div>
  );
}